<script setup lang="ts">
import type { EventMediaMode } from '~/shared/lib/events-routes.const';
import type { GalleryListItem } from '~/shared/api/client/galleries/galleries.dto';
import {
  GalleryCard,
  formatGalleryDate,
  getGalleryCover,
} from '~/entities/gallery-card';
import { EVENTS_GALLERY_DETAIL_CONFIG } from '../lib/gallery-detail.const';

const props = defineProps<{
  mode: EventMediaMode;
  galleries: GalleryListItem[];
}>();

const config = computed(() => EVENTS_GALLERY_DETAIL_CONFIG[props.mode]);
</script>

<template>
  <section v-if="galleries.length > 0" :class="$style.section">
    <div :class="$style.inner">
      <div :class="$style.header">
        <h2 :class="$style.title">Отчеты с других мероприятий</h2>
        <Button
          theme="outline"
          size="standard"
          :to="{ name: config.listRouteName }"
          :class="$style.button"
        >
          Смотреть все
        </Button>
      </div>

      <div :class="$style.grid">
        <GalleryCard
          v-for="item in galleries"
          :key="item.id"
          variant="ghost"
          :image-url="getGalleryCover(item)"
          :caption="formatGalleryDate(item)"
          :title="item.description ?? item.name"
          :to="{
            name: config.detailRouteName,
            params: { id: item.id },
          }"
        />
      </div>
    </div>
  </section>
</template>

<style module lang="scss">
.section {
  padding: 0 110px 40px;
}

.inner {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 860px;
  background-color: var(--color-gray-bg);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2x);
  min-height: 64px;
  padding: 0 20px;
  border-bottom: 1px solid var(--color-gray-line);
}

.title {
  @include text_h5;
  margin: 0;
  color: var(--color-black-1);
}

.button {
  flex-shrink: 0;
  min-height: 40px;
  background-color: var(--color-white);
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  > :nth-child(3n) {
    border-right: none;
  }
}

@media (max-width: 1379px) {
  .section {
    padding-left: 20px;
    padding-right: 20px;
  }

  .inner {
    max-width: none;
  }
}

@media (max-width: 599px) {
  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > :nth-child(2n),
    > :nth-child(3) {
      border-right: none;
    }

    > :nth-child(3) {
      grid-column: 1 / -1;
      --gallery-card-media-ratio: 20 / 9;
    }
  }
}

@media (max-width: 479px) {
  .section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .header {
    align-items: flex-start;
    flex-direction: column;
    padding-top: 20px;
    padding-bottom: 20px;
  }

  .title {
    @include text_h5_mobile;
  }

  .grid {
    grid-template-columns: 1fr;

    > :first-child {
      border-right: none;
    }

    > :nth-child(n + 2) {
      display: none;
    }
  }
}
</style>
